import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Grid,
  Typography,
  Stack,
  FormControl,
  FormHelperText,
  TextField,
  Paper,
  Autocomplete,
  Select,
  MenuItem,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import '../../Styles/CategoryStyles.css';
import * as Yup from 'yup';
import { categoryStyles } from '../../Styles/CategoryFormStyles';
import {
  useItemAddCategory,
  useEditItemCategory,
} from '../../Query/ItemCategoryQuery';
import {
  CreateItemCategoryRequest,
  CreateItemCategoryRequestStoreTypeEnum,
} from '../../../client-sdk';
import { primary } from '../../../theme';
import { useAllStores } from '../../Query/ManageStoreQuery';
import { useManageCmsParams } from '../ManageChoiceGroup/useManageCmsParams';

const ItemCategoryForm = ({
  mode,
  onCancel,
  initialData,
}: {
  mode: 'add' | 'edit';
  onCancel: () => void;
  initialData?: {
    id?: string;
    englishName: string;
    arabicName: string;
    addedBy: string;
    storeId?: string;
    storeType?: string;
  };
}) => {
  const { t } = useTranslation();
  const { mutate } = useItemAddCategory();
  const { mutate: editItemCategory } = useEditItemCategory();
  const [loading, setLoading] = useState(false);
  const [storeOptions, setStoreOptions] = useState<{ label: string; value: string; storeType: string }[]>([]);
  const { params } = useManageCmsParams();
  const isReady = Boolean(params && Object.keys(params).length > 0);
  const memoizedParams = useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);
  // Fetch stores data
  const { data: storesData } = useAllStores(memoizedParams);

  useEffect(() => {
    if (storesData && Array.isArray(storesData.data)) {
      const options = storesData.data.map((store: any) => ({
        value: store.id,
        label: store.nameEnglish,
        storeType: store.restaurantDetails?.storeType || '',
      }));
      setStoreOptions(options);
    } else {
      setStoreOptions([]);
    }
  }, [storesData]);

  console.log('initialData', initialData);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      englishName: initialData?.englishName || '',
      arabicName: initialData?.arabicName || '',
      storeId: initialData?.storeId || '',
      storeType: initialData?.storeType || '',
    },
    validationSchema: Yup.object().shape({
      englishName: Yup.string()
        .required(t('itemCategory.form.validation.requiredCategoryNameEnglish'))
        .max(100, t('itemCategory.form.validation.categoryNameEnglishMaxLength'))
        .matches(
          /^[A-Za-z][A-Za-z0-9\s!@#$%^&*(),.?":{}|<>_-]*$/,
          t('itemCategory.form.validation.categoryNameEnglishFormat')
        )
        .test(
          'not-only-numbers',
          t('itemCategory.form.validation.categoryNameEnglishNotOnlyNumbers'),
          (value) => {
            if (!value) return false;
            return !/^\d+$/.test(value.trim());
          }
        )
        .test(
          'not-only-spaces',
          t('itemCategory.form.validation.categoryNameEnglishNotOnlySpaces'),
          (value) => {
            if (!value) return false;
            return value.trim().length > 0;
          }
        ),
      arabicName:  Yup.string()
        .required(t('itemCategory.form.validation.requiredCategoryNameArabic'))
        .max(50, t('itemCategory.form.validation.categoryNameArabicMaxLength'))
        .matches(
          /^[\u0600-\u06FF][\u0600-\u06FF\s]*$/,
          t('itemCategory.form.validation.categoryNameArabicFormat')
        )
        .test(
          'not-only-spaces',
          t('itemCategory.form.validation.categoryNameArabicNotOnlySpaces'),
          (value) => {
            if (typeof value !== 'string') return false;
            return value.trim().length > 0;
          }
        ),
      storeId: mode === 'add' ? Yup.string().required(t('itemCategory.form.validation.requiredStore')) : Yup.string(),
      storeType: mode === 'add' ? Yup.string().required(t('itemCategory.form.validation.requiredStoreType')) : Yup.string(),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const itemCategoryRequest: CreateItemCategoryRequest = {
          addedBy: 'Merchant',
          nameEnglish: values.englishName,
          nameArabic: values.arabicName,
          storeId: values.storeId,
          storeType: values.storeType as any,
        };
        const itemEditCategory: CreateItemCategoryRequest = {
          addedBy: 'Merchant',
          nameEnglish: values.englishName,
          nameArabic: values.arabicName,
          storeType: (initialData?.storeType ?? '') as CreateItemCategoryRequestStoreTypeEnum,
          storeId: (initialData?.storeId ?? ''),
        };
        if (mode === 'edit' && initialData?.id) {
          editItemCategory(
            { id: initialData.id, data: itemEditCategory },
            {
              onSuccess: () => {
                formik.resetForm();
                setLoading(false);
                onCancel();
              },
              onError: (error: any) => {
                console.error('Edit Error:', error);
                setLoading(false);
              },
            }
          );
        } else {
          mutate({ data: itemCategoryRequest }, {
            onSuccess: () => {
              formik.resetForm();
              setLoading(false);
              onCancel();
            },
            onError: () => {
              setLoading(false);
            },
          });
        }
      } catch (error) {
        console.error('Error during submission:', error);
        setLoading(false);
      }
    },
  });

  const handleNameChange =
    (field: 'englishName' | 'arabicName') =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (value.length > 50) {
          formik.setFieldError(field, t('itemCategory.form.validation.categoryNameMaxLength'));
        } else {
          formik.setFieldValue(field, value);
          formik.setFieldError(field, undefined);
        }
      };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 4,
        mt: 2,
        bgcolor: '#fff',
        borderRadius: 1,
        border: '1px solid #ddd',
      }}
    >
      {/* {loading && (
        <Box sx={categoryStyles.loaderBox}>
          <CircularProgress />
        </Box>
      )} */}

      <Box component="form" noValidate onSubmit={formik.handleSubmit}>
        <Grid
          container
          direction="column"
          spacing={3}
          maxWidth="1200px"
          sx={{ width: '100%' }}
        >
          {/* Store Type Selection - Only show in add mode */}
          {mode === 'add' && (
            <Grid>
              <Box sx={categoryStyles.labelBox}>
                <Typography sx={categoryStyles.labelText}>{t('storeManagement.form.storeType')}*</Typography>
                <FormControl fullWidth error={formik.touched.storeType && Boolean(formik.errors.storeType)}>
                  <Select
                    name="storeType"
                    value={formik.values.storeType}
                    displayEmpty
                    onChange={(e: any) => {
                      const value = e.target.value;
                      formik.setFieldValue('storeType', value);
                      formik.setFieldValue('storeId', '');
                      if (value) {
                        formik.setFieldError('storeType', undefined);
                        formik.setFieldError('storeId', undefined);
                      }
                      formik.setFieldTouched('storeType', true, false);
                      setTimeout(() => {
                        formik.setFieldTouched('storeType', true, false);
                        if (value) {
                          formik.validateField('storeType');
                        }
                      }, 100);

                    }}
                    sx={{ minWidth: 400 }}
                  >
                    <MenuItem value="" disabled>
                      <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{t('itemCategory.form.selectDefault')}</span>
                    </MenuItem>
                    <MenuItem value="Restaurant">{t("common.restaurant")}</MenuItem>
                    <MenuItem value="GroceryStore">{t("common.groceryStore")}</MenuItem>
                    <MenuItem value="PharmacyStore">{t("common.pharmacyStore")}</MenuItem>
                  </Select>
                  {formik.touched.storeType && formik.errors.storeType && (
                    <FormHelperText error sx={categoryStyles.errorText}>
                      {formik.errors.storeType}
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Grid>
          )}

          {/* Store Selection - Only show in add mode */}
          {mode === 'add' && (
            <Grid>
              <Box sx={categoryStyles.labelBox}>
                <Typography sx={categoryStyles.labelText}>
                  {t('itemCategory.form.selectStore')}
                </Typography>
                <FormControl
                  fullWidth
                  error={
                    formik.touched.storeId &&
                    Boolean(formik.errors.storeId)
                  }
                  sx={{
                    alignItems: 'flex-start',
                    '& .MuiInput-root': {
                      width: '400px',
                      backgroundColor: '#fff',
                      borderRadius: '4px',
                      padding: '8px 12px',
                      '&:hover:not(.Mui-disabled):before': {
                        borderBottom: '2px solid rgba(0, 0, 0, 0.42)',
                      },
                      '&:before': {
                        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                      },
                      '& .MuiInputBase-root': {
                        width: '400px', // 👈 you can also add here for extra safety
                      },
                    },
                  }}
                >
                  <Autocomplete
                    options={storeOptions.filter(store => !formik.values.storeType || store.storeType === formik.values.storeType)}
                    getOptionLabel={(option) => option.label || ''}
                    value={storeOptions.find((opt) => opt.value === formik.values.storeId) || null}
                    onChange={(_, newValue) => {
                      const value = newValue ? newValue.value : '';
                      formik.setFieldValue('storeId', value);


                    }}
                    // disabled={!formik.values.storeType}
                    sx={{
                      width: '400px',
                      '& .MuiInputBase-root': {
                        fontSize: 14,
                        color: 'text.primary',
                        height: '45px',
                        width: '400px',
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        // placeholder={!formik.values.storeType ? "Select store type first" : "Select a store"}
                        placeholder={t('itemCategory.form.placeholders.selectStore')}
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />
                  {formik.touched.storeId && formik.errors.storeId && (
                    <FormHelperText error sx={categoryStyles.errorText}>
                      {formik.errors.storeId}
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Grid>
          )}

          {/* English Name */}
          <Grid>
            <Box sx={categoryStyles.labelBox}>
              <Typography sx={categoryStyles.labelText}>
                {t('itemCategory.form.categoryNameEnglish')}
              </Typography>
              <FormControl
                fullWidth
                error={
                  formik.touched.englishName &&
                  Boolean(formik.errors.englishName)
                }
                sx={{
                  alignItems: 'flex-start',
                  '& .MuiInput-root': {
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: '2px solid rgba(0, 0, 0, 0.42)',
                    },
                    '&:before': {
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                    },
                  },
                }}
              >
                <TextField
                  placeholder={t('itemCategory.form.placeholders.categoryNameEnglish')}
                  name="englishName"
                  value={formik.values.englishName}
                  onChange={handleNameChange('englishName')}
                  onBlur={formik.handleBlur}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: 14,
                      color: 'text.primary',
                      width: '400px',
                      height: '45px',
                      marginBottom: '8px',
                    },
                    '& .MuiFormLabel-root': {
                      fontSize: 14,
                    },
                  }}
                />
                {formik.touched.englishName && formik.errors.englishName && (
                  <FormHelperText error sx={categoryStyles.errorText}>
                    {formik.errors.englishName}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>
          </Grid>

          {/* Arabic Name */}
          <Grid>
            <Box sx={categoryStyles.labelBox}>
              <Typography sx={categoryStyles.labelText}>
                {t('itemCategory.form.categoryNameArabic')}
              </Typography>
              <FormControl
                fullWidth
                error={
                  formik.touched.arabicName && Boolean(formik.errors.arabicName)
                }
                sx={{
                  alignItems: 'flex-start',
                  '& .MuiInput-root': {
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: '2px solid rgba(0, 0, 0, 0.42)',
                    },
                    '&:before': {
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                    },
                  },
                }}
              >
                <TextField
                  placeholder={t('itemCategory.form.placeholders.categoryNameArabic')}
                  name="arabicName"
                  value={formik.values.arabicName}
                  onChange={handleNameChange('arabicName')}
                  onBlur={formik.handleBlur}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: 14,
                      color: 'text.primary',
                      width: '400px',
                      height: '45px',
                    },
                    '& .MuiFormLabel-root': {
                      fontSize: 14,
                    },
                  }}
                />
                {formik.touched.arabicName && formik.errors.arabicName && (
                  <FormHelperText error sx={categoryStyles.errorText}>
                    {formik.errors.arabicName}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>
          </Grid>

          {/* Buttons */}
          <Grid>
            <Box sx={categoryStyles.buttonsWrapper}>
              <Box sx={{ minWidth: { xs: '100%', sm: '250px' } }} />
              <Stack
                direction="row"
                spacing={2}
                justifyContent="flex-start"
                sx={{ width: '100%' }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ bgcolor: primary.main }}
                  disabled={
                    mode === 'edit' &&
                    (!formik.dirty || !formik.isValid || loading)
                  }
                >
                  {loading ? t('itemCategory.form.saving') : mode === 'edit' ? t('itemCategory.form.update') : t('itemCategory.form.save')}
                </Button>
                <Button
                  variant="contained"
                  sx={categoryStyles.cancelButton}
                  onClick={() => {
                    formik.resetForm();
                    onCancel();
                  }}
                >
                  {t('itemCategory.form.cancel')}
                </Button>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default ItemCategoryForm;
